import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useDeleteRole } from 'src/features/roles/api/useDeleteRole';
import { Role } from 'src/features/roles/utils/types';
import { rolesMessages } from 'src/features/roles/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import { LIST_ROLES_RESOURCE } from 'src/features/roles/api/useListRoles';
import { generalErrorMessages } from 'src/utils/messages';

interface RoleFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedRole: Role;
    children?: React.ReactNode;
}

function DeleteRoleFormModal<TData>({ selectedRole, semantics, children }: RoleFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { mutate: deleteRole, isPending: isPendingDelete } = useDeleteRole({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_ROLES_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(rolesMessages.deleteRoleSuccess),
                description: formatMessage(rolesMessages.deleteRoleSuccessDescription, {
                    entry: selectedRole.name,
                }),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;

            const errorDetails =
                error.response?.data?.code === 'role_deletion_error'
                    ? formatMessage(rolesMessages.deleteRoleError)
                    : error.message;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const onSubmit = () => deleteRole({ roleId: selectedRole.id, parkingId });

    return (
        <Dialog modal open={isModalOpen} onOpenChange={setIsModalOpen}>
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={ICON_MAP.DELETE} />
                        <div>
                            <DialogTitle>
                                <FormattedMessage
                                    id={generalMessages.deleteEntry.id}
                                    defaultMessage={generalMessages.deleteEntry.defaultMessage}
                                    values={{ entry: semantics?.entryName?.toLowerCase() }}
                                />
                            </DialogTitle>
                            <DialogDescription>
                                <FormattedMessage
                                    id={rolesMessages.deleteRoleSubtitle.id}
                                    defaultMessage={rolesMessages.deleteRoleSubtitle.defaultMessage}
                                    values={{
                                        entry: selectedRole.name,
                                    }}
                                />
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <p className="text-gray-500">
                        <FormattedMessage
                            id={rolesMessages.confirmDeleteRole.id}
                            defaultMessage={rolesMessages.confirmDeleteRole.defaultMessage}
                            values={{
                                entry: selectedRole.name,
                            }}
                        />
                    </p>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructiveLight"
                        disabled={isPendingDelete}
                        type="submit"
                        onClick={onSubmit}
                        className="flex-1"
                    >
                        {isPendingDelete && (
                            <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />
                        )}
                        <FormattedMessage
                            id={generalMessages.deleteEntry.id}
                            defaultMessage={generalMessages.deleteEntry.defaultMessage}
                            values={{ entry: semantics?.entryName?.toLowerCase() }}
                        />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default DeleteRoleFormModal;
